package dev.kuml.portal.web.editor

import dev.kuml.portal.web.i18n.format.LocaleFormat

/**
 * Pure, DOM-free helpers for R-8's "attach a context file" control in [EditorScreen] -- see
 * `AiContextFileTest`. Every check here is a CONVENIENCE, never a security boundary: a caller
 * hitting `POST /api/ai/context` directly bypasses all of it, so the real, load-bearing validation
 * is `dev.kuml.portal.server.ai.AiContextValidator` server-side. This object exists purely to give
 * the user fast, friendly feedback before a network round trip.
 */
public object AiContextFile {
    /** Mirrors the SERVER's default `maxAiContextBytes` (see
     * `dev.kuml.portal.server.config.PortalServerConfig.maxAiContextBytes`'s KDoc) purely so
     * [tooLarge] can short-circuit an obviously-oversized file locally. If an operator raises the
     * server-side cap, this constant simply becomes a (harmless) tighter local pre-check --
     * [tooLarge] rejecting nothing does not, and must not, change what the server itself enforces. */
    public const val MAX_BYTES: Int = 65_536

    /** `<input type="file" accept="...">` value -- narrows the OS file picker's default view. Not
     * a security boundary (see class KDoc): a user can still pick "All Files" and select anything. */
    public val ACCEPT_ATTRIBUTE: String =
        ".csv,.json,.md,.markdown,.txt,text/csv,application/json,text/markdown,text/plain"

    /** Derives the wire-format value `PortalApi.uploadAiContext` should send for [fileName]'s
     * extension, or `null` if the extension is not one of the four this codebase's
     * `AiContextFormat` enum recognizes -- callers refuse to upload in that case rather than
     * guessing. Case-insensitive; only the LAST `.`-separated segment is considered. */
    public fun formatForFileName(fileName: String): String? {
        val dot = fileName.lastIndexOf('.')
        if (dot < 0 || dot == fileName.length - 1) return null
        return when (fileName.substring(dot + 1).lowercase()) {
            "csv" -> "csv"
            "json" -> "json"
            "md", "markdown" -> "markdown"
            "txt" -> "text"
            else -> null
        }
    }

    /** `true` when [bytes] exceeds [MAX_BYTES] -- exactly at the limit is still allowed (`<=`, not
     * `<`), matching the server's own `raw.size.toLong() > maxBytes` (strictly-greater) rejection
     * condition. */
    public fun tooLarge(bytes: Int): Boolean = bytes > MAX_BYTES

    /** R-18: size label for the confirmation chip, delegating the decimal-separator choice to
     * [LocaleFormat.sizeLabel] (`"12,4 KB"` in German, `"12.4 KB"` in English), e.g. for 12 700
     * bytes, or `"512 B"` below 1 000 bytes either way. Deliberately decimal (1 000-based),
     * matching how a typical OS file browser labels small text files, not the binary 1 024-based
     * KiB this file's own [MAX_BYTES]/the server's byte caps use internally. */
    public fun sizeLabel(
        bytes: Int,
        format: LocaleFormat,
    ): String = format.sizeLabel(bytes)

    /** Label for the attached-file chip, e.g. `"schema.csv (12.4 KB)"`. */
    public fun chipLabel(
        fileName: String,
        bytes: Int,
        format: LocaleFormat,
    ): String = "$fileName (${sizeLabel(bytes, format)})"
}
